import React, { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CreditApplicationStatus, UnderwritingOffer } from '_gql'

import { useCanary } from '@bill/canary.react'
import { FormattedMessage } from '@divvy-web/i18n'
import WistiaPlayer from '@divvy-web/skylab.wistiaplayer'

import PollingPageContent from '@sputnik/pages/PostApplicationPages/PollingPageContent'
import { GetDecisionAndStatusForPolling } from '@sputnik/pages/PostApplicationPages/SubmittingApplicationPollingPage/GetDecisionAndStatusForPolling.gql'
import { MAJOR_ACCOUNT_CHANGE, MIGRATION, PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

export interface ReviewingApplicationPollingPageContentProps {
  stopPolling: () => void
  decisionAndStatusData: GetDecisionAndStatusForPolling
  isReadyToNavigate: boolean
}

const ReviewingApplicationPollingPageContent = ({
  stopPolling,
  decisionAndStatusData,
  isReadyToNavigate,
}: ReviewingApplicationPollingPageContentProps) => {
  const navigate = useNavigate()
  const isUWPageRedesign = useCanary('rollout-uw-page-redesign')
  const { appId } = useParams()
  const appPath = `/app/${appId}`
  const { creditApplication, underwritingDeclined, frozenAuthorizedSigner, decisions } = decisionAndStatusData ?? {}
  const { status, recordType } = creditApplication ?? {}

  const isPreSubmitStatus =
    !!status &&
    [
      CreditApplicationStatus.NOT_STARTED,
      CreditApplicationStatus.IN_PROGRESS,
      CreditApplicationStatus.AWAITING_SIGNATURE,
    ].includes(status)
  const isMajorAccountChange = recordType === MAJOR_ACCOUNT_CHANGE
  const isMigration = recordType === MIGRATION
  const hasNoDecisions = !decisions || decisions?.length === 0 || decisions?.length > 3
  const hasSingleDecision = decisions?.length === 1
  const hasMultipleDecisions = decisions && decisions?.length >= 2
  const hasSelectedDecision = !!decisions?.find((decision) => decision?.userSelectedAt)
  const isRejectedStatus = status === CreditApplicationStatus.REJECTED
  const isMoreInfoNeededStatus = status === CreditApplicationStatus.MORE_INFO_NEEDED
  const standardLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.FIXED_LINE)
  const variableLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.VARIABLE_LINE)
  const securedLineOffer = decisions?.find((decision) => decision?.offerType === UnderwritingOffer.SECURED_LINE)
  const offersPath = `/app/${appId}/${PATHNAME_CONSTS.OFFERS_PATH}`

  /* tracking start */

  const { track } = useWebTracking()

  useEffect(() => {
    track('se_signup__processing_application__presented')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /* tracking end */

  if (isReadyToNavigate) {
    window.sessionStorage.removeItem('submissionPendingUrl')
    stopPolling()
    switch (true) {
      case isMajorAccountChange:
        navigate(PATHNAME_CONSTS.DASHBOARD_PATH, { replace: true })
        break
      case isMigration:
        navigate(`${appPath}/${PATHNAME_CONSTS.MIGRATION_SUCCESS_PATH}`, { replace: true })
        break
      case frozenAuthorizedSigner:
        navigate(`${appPath}/${PATHNAME_CONSTS.FROZEN_CREDIT_PATH}`, { replace: true })
        break
      case underwritingDeclined || isRejectedStatus:
        navigate(`/app/${appId}/${PATHNAME_CONSTS.NO_OFFER_PATH}`, { replace: true })
        break
      case isMoreInfoNeededStatus:
        navigate(PATHNAME_CONSTS.DASHBOARD_PATH, { replace: true })
        break
      case hasNoDecisions:
        navigate(`${appPath}/${PATHNAME_CONSTS.WORKING_OUT_DETAILS_PATH}`, { replace: true })
        break
      case hasMultipleDecisions && !hasSelectedDecision:
        navigate(offersPath, { replace: false })
        break
      case hasSingleDecision && !hasSelectedDecision:
        if (isUWPageRedesign) {
          navigate(offersPath, { replace: false })
        } else if (standardLineOffer) {
          navigate(`${offersPath}/${PATHNAME_CONSTS.STANDARD_CREDIT_LINE_PATH}`, { replace: false })
        } else if (variableLineOffer) {
          navigate(`${offersPath}/${PATHNAME_CONSTS.VARIABLE_CREDIT_LINE_PATH}`, { replace: false })
        } else if (securedLineOffer) {
          navigate(`${offersPath}/${PATHNAME_CONSTS.PREPAY_OFFER_PATH}`, {
            replace: false,
          })
        }
        break
      case isPreSubmitStatus:
        navigate(`${appPath}/${PATHNAME_CONSTS.SUBMISSION_ERROR_PATH}`, { replace: true })
        break
      default:
        navigate(PATHNAME_CONSTS.DASHBOARD_PATH, { replace: true })
        break
    }
  }

  return (
    <PollingPageContent
      mainText={
        <FormattedMessage
          defaultMessage={`{isPreSubmitStatus, select, 
            true {We're submitting your application}
            other {We're reviewing your application}
          }`}
          id='sputnik.ReviewingApplicationPollingPageContent__S737h6'
          values={{ isPreSubmitStatus }}
        />
      }
      subText={
        <FormattedMessage
          defaultMessage='We should have a decision for you soon.'
          id='sputnik.ReviewingApplicationPollingPageContent__zrXnI+'
        />
      }
    >
      <WistiaPlayer
        autoplay
        videoId='3371q71man'
      />
    </PollingPageContent>
  )
}

export default ReviewingApplicationPollingPageContent
